import ColorThief from "colorthief";
import type { RGBArray } from "../design/types";

/**
 * Gets the dominant color of an image. Avoids issues with low contrast colors.
 * @reference https://github.com/lokesh/color-thief/issues/40
 * @param image - The image to get the dominant color from.
 * @returns A Promise that resolves to an RGB array.
 */
export function getDominantColor(image: HTMLImageElement): Promise<RGBArray>;
export function getDominantColor(image: string): Promise<RGBArray>;
export function getDominantColor(
  image: HTMLImageElement | string
): Promise<RGBArray> {
  if (image instanceof HTMLImageElement) {
    return Promise.resolve(getDominantColorFromImage(image));
  } else {
    return getDominantColorFromImageUrl(image);
  }
}

function getDominantColorFromImage(image: HTMLImageElement): RGBArray {
  try {
    const colorThief = new ColorThief();
    const baseColor = colorThief.getColor(image);
    return baseColor;
  } catch {
    return [255, 255, 255];
  }
}

function getDominantColorFromImageUrl(url: string): Promise<RGBArray> {
  return new Promise((resolve, reject) => {
    const imgElement = document.createElement("img");
    imgElement.crossOrigin = "anonymous";

    imgElement.onload = () => {
      try {
        const baseColor = new ColorThief().getColor(imgElement);
        resolve(baseColor);
      } catch {
        resolve([255, 255, 255]);
      }
    };

    imgElement.onerror = () => reject(new Error("Failed to load image"));
    imgElement.src = url;
  });
}
